import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { applyTenantTheme } from '../lib/tenantTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAL = '#14B8A6';

const PortalLoginPage = () => {
  const { slug } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    axios.get(`${API}/portal/branding/${slug}`)
      .then(r => {
        setBranding(r.data);
        // Wire the same CSS vars used by the authed app so the login
        // CTA, focus rings, and any downstream splash chrome pick up
        // the tenant colour on the login page too (spec item 3).
        applyTenantTheme(
          r.data?.custom_color || '',
          r.data?.custom_logo_url || '',
          r.data?.custom_icon_url || '',
          {},
          {},
          r.data?.custom_accent_text_color || '',
        );
      })
      .catch(() => setBranding(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const primaryColor = branding?.custom_color || TEAL;
  const companyName = branding?.company?.name || slug;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const user = await login(email, password);
      if (user?.domain_id) navigate('/my-domain');
      else if (user?.role === 'master_admin') navigate('/select-mode');
      else navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <Loader2 className="w-8 h-8 text-white/20 animate-spin" />
    </div>
  );

  if (!branding) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white mb-2">Portal Not Found</h1>
        <p className="text-white/40 text-sm">This portal URL does not exist or has been deactivated.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 p-6">
      <div className="w-full max-w-sm">
        {/* Company branding */}
        <div className="text-center mb-10">
          {branding.custom_logo_url ? (
            <img src={branding.custom_logo_url} alt={companyName} className="h-14 w-auto object-contain mx-auto mb-4" />
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ backgroundColor: primaryColor + '22' }}>
              <span className="text-2xl font-black" style={{ color: primaryColor }}>
                {companyName?.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <h1 className="text-xl font-bold text-white">{companyName}</h1>
          <p className="text-white/30 text-sm mt-1">Call Intelligence Portal</p>
        </div>

        {/* Login form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="text-white font-semibold mb-5">Sign in</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-white/40 mb-1.5">Extension Login</label>
              <input
                type="text"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={`e.g. 9922@${branding.yabbit_domain}`}
                required
                autoFocus
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none transition-colors"
                style={{ '--tw-ring-color': primaryColor }}
                onFocus={e => e.target.style.borderColor = primaryColor + '80'}
                onBlur={e => e.target.style.borderColor = ''}
                data-testid="portal-email"
              />
            </div>

            <div>
              <label className="block text-xs text-white/40 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none pr-10"
                  onFocus={e => e.target.style.borderColor = primaryColor + '80'}
                  onBlur={e => e.target.style.borderColor = ''}
                  data-testid="portal-password"
                />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 text-white font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2"
              style={{ backgroundColor: primaryColor }}
              data-testid="portal-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>
        </div>

        <p className="text-center text-white/15 text-xs mt-6">
          Powered by Yabbit VoiceFabric
        </p>
      </div>
    </div>
  );
};

export default PortalLoginPage;
